import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info, X } from "lucide-react";

/**
 * Click/tap-accessible contextual explanation beside an approach, loading
 * pattern, cycle role, type or phase. Progressive disclosure: short text first,
 * then evidence sources. Works by click/tap — never hover-only.
 *
 * The panel + backdrop are rendered through a portal to document.body so they
 * are never trapped inside a `.fl-card` stacking context (will-change:transform)
 * and always paint above sibling cards.
 */
export const InfoButton = ({ entry, evidence = {}, testid }) => {
  const btnRef = useRef(null);
  const [pos, setPos] = useState(null); // {top,left}
  const [more, setMore] = useState(false);
  const open = !!pos;

  const toggle = (e) => {
    e.stopPropagation();
    if (open) { setPos(null); setMore(false); return; }
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const W = 320, maxH = 360, m = 12;
    let left = r.left;
    if (left + W > window.innerWidth - m) left = window.innerWidth - W - m;
    left = Math.max(m, left);
    let top = r.bottom + 6;
    if (top + maxH > window.innerHeight - m) {
      // flip above the button when it would overflow the bottom
      const above = r.top - maxH - 6;
      top = above >= m ? above : Math.max(m, window.innerHeight - maxH - m);
    }
    setPos({ top, left });
  };
  const close = () => { setPos(null); setMore(false); };

  if (!entry) return null;
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={`About ${entry.label}`}
        onClick={toggle}
        className="text-zinc-400 hover:text-[#f47a20] transition-colors"
        data-testid={testid || `info-${entry.key}`}
      >
        <Info size={14} />
      </button>
      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[90]" onClick={close} data-testid={`info-backdrop-${entry.key}`} />
          <div
            role="dialog"
            className="fixed z-[91] w-80 max-w-[calc(100vw-24px)] max-h-[360px] overflow-y-auto rounded-xl border border-white/15 bg-[#17171b] p-4 shadow-2xl"
            style={{ top: pos.top, left: pos.left }}
            data-testid={`info-panel-${entry.key}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm font-bold text-[#f47a20]">{entry.label}</div>
              <button type="button" onClick={close} className="text-zinc-500 hover:text-white"><X size={14} /></button>
            </div>
            <p className="mt-2 text-xs text-zinc-200 leading-relaxed">{entry.what}</p>
            <p className="mt-2 text-xs text-zinc-400"><span className="text-zinc-500">Use: </span>{entry.use}</p>
            <p className="mt-1 text-xs text-amber-300/90"><span className="text-zinc-500">Does not decide: </span>{entry.not_decide}</p>
            {entry.example && <p className="mt-1 text-xs text-zinc-400 italic">e.g. {entry.example}</p>}
            {!more ? (
              <button type="button" onClick={() => setMore(true)} className="mt-2 text-[11px] text-[#f47a20] hover:underline" data-testid={`info-more-${entry.key}`}>
                More detail / evidence →
              </button>
            ) : (
              <div className="mt-2 border-t border-white/10 pt-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Sources</div>
                {(entry.sources || []).length === 0 && <div className="text-[11px] text-zinc-500">No source linked.</div>}
                {(entry.sources || []).map((sid) => {
                  const ev = evidence[sid];
                  return (
                    <div key={sid} className="text-[11px] text-zinc-300 mb-1.5">
                      <span className="text-zinc-500">{sid}</span> · {ev?.citation || sid}
                      {ev?.url && <> · <a href={ev.url} target="_blank" rel="noreferrer" className="text-[#f47a20] hover:underline">link</a></>}
                      {ev?.status && <span className="text-zinc-600"> ({ev.status})</span>}
                    </div>
                  );
                })}
                <div className="text-[10px] text-zinc-600 mt-1">Review status: {entry.review_status} · {entry.last_review}</div>
              </div>
            )}
          </div>
        </>,
        document.body,
      )}
    </>
  );
};

export default InfoButton;
